import api from "@/api";
import { getApiErrorStatus } from "@/helpers/apiErrorShape";
import { Conversation } from "@/types/Conversation.js";
import { Project } from "@/types/Project.js";
import { EntitySearchResponse } from "@/types/search/EntitySearchResponse";
import type { IndexableEntityType } from "@/types/search/IndexableEntityTypes";
import { SearchRequest } from "@/types/search/SearchRequest";

/**
 * LSP-16659: what the Session and Project media grids show when no View filter is applied — photos,
 * videos AND documents. `/search/media` keeps snapshot + recording as ITS default, because shipped
 * native builds call it without `contentTypes` and decode every non-recording row as a photo, so
 * documents are strictly opt-in per request.
 */
export const ENTITY_TIMELINE_DEFAULT_CONTENT_TYPES: readonly IndexableEntityType[] = Object.freeze(["snapshot", "recording", "document"]);

export abstract class BaseEntityService<T extends Conversation | Project> {
	/**
	 * Gets the entity
	 * @param entityId - The id of the entity
	 * @returns The entity
	 */
	public abstract getEntity(entityId: string): Promise<T>;

	/**
	 * Deletes the entity
	 * @param entityId - The id of the entity
	 */
	public abstract deleteEntity(entityId: string): Promise<void>;

	/**
	 * Gets the media for timeline for the entity
	 * @param searchRequest - The search request
	 * @returns The media for the timeline
	 */
	public async getTimeline(searchRequest: SearchRequest, isAuthenticated: boolean): Promise<EntitySearchResponse> {
		// LSP-16659: the default is applied to a COPY of the outgoing request, here and only here. The
		// caller's request is the page's filter state, where an empty `contentTypes` means "no View
		// filter" — storing the default there would read as a narrowing user filter (userFiltersNarrowResults)
		// and switch off the delta reload, the failed-upload warnings and the async reminder. It must not
		// move into api.mediaSearch either: ProjectChecklists calls that directly for the checklist media
		// picker, which only accepts photos and videos.
		const injectedDefault = !searchRequest?.contentTypes?.length;
		const outgoingRequest: SearchRequest = injectedDefault
			? { ...searchRequest, contentTypes: [...ENTITY_TIMELINE_DEFAULT_CONTENT_TYPES] }
			: { ...searchRequest };
		const runMediaSearch = (request: SearchRequest): Promise<EntitySearchResponse> => api.mediaSearch(request, isAuthenticated);
		try {
			return await runMediaSearch(outgoingRequest);
		} catch (error) {
			// Deploy-order / rollback safety: an API that predates LSP-16659 rejects `document` with HTTP
			// 400, which would blank the whole grid (photos, videos, public shares). Only the default THIS
			// method injected earns a retry — a user's own View filter, any other status, or a failing
			// retry all propagate exactly as before. The retry sends the caller's request unchanged, so
			// the API applies its own default (photos + videos).
			const status = getApiErrorStatus(error);
			if (!injectedDefault || status !== 400) throw error;
			console.warn(
				"BaseEntityService.getTimeline: media search rejected the default content types (HTTP 400); retrying once without documents — documents were dropped for compatibility with an API that predates LSP-16659",
				{
					status,
					projectId: searchRequest?.projectId ?? null,
					parentIds: searchRequest?.parentIds ?? null,
					shareId: searchRequest?.shareId ?? null,
					isAuthenticated,
					droppedContentTypes: ["document"]
				}
			);
			return await runMediaSearch({ ...searchRequest });
		}
	}
}
